// The house ranking. BACKLOG D4, DRAFT_MODES §4.5.
//
// "A manager's list is a prefix of the house list, not a replacement for it."
// Every platform falls back to one — Yahoo to its default ranking, ESPN to the
// ESPN draft list, NFL.com to its own, MFL to ADP — and so do we, because a
// manager who ranked nobody still has to end up with a team they are not
// embarrassed by.
//
// A FIRST-CLASS, VISIBLE ARTIFACT, and that is the design's word: "people will
// want to read it before deciding whether ranking is worth their evening." So
// it is served, not hidden inside the draft, and it says **what it was built
// from** — because a ranking whose inputs are unstated is one nobody can judge.
//
// THAT INPUT IS LAST SEASON'S POINTS. The house projections (Epic F, running
// since PLAN-2026-09-25 F1) are made a week at a time — the window in play and
// the next — and a draft ranks a season, so this is computed from real stored
// stat lines through the league's own scoring, and the artifact says so in
// `basis` so nobody mistakes it for a forecast. A season-long projection would
// change the basis, and this file is where it changes.

import { z } from 'zod';

export const RankedPlayer = z.object({
  playerId: z.string(),
  name: z.string(),
  positions: z.array(z.string()),
  proTeam: z.string().nullable(),
  /** 1-based. The list's own order, so a client need not re-derive it. */
  rank: z.number().int().min(1),
  /**
   * The number this rank was sorted on, in the scoring used.
   *
   * Shown, not hidden: "why is he above him" is the first question anybody asks
   * of a ranking, and a list that cannot answer it is one people stop trusting.
   * Null for a player the basis had nothing for — a rookie, or somebody who
   * did not play — who is ranked below everybody it did.
   */
  value: z.number().nullable(),
});
export type RankedPlayer = z.infer<typeof RankedPlayer>;

export const HouseRanking = z.object({
  sport: z.string(),
  season: z.number().int(),
  /**
   * What this was built from, in words, for the screen that shows it.
   *
   * REQUIRED, and it is the point of the artifact. A ranking is a claim about
   * the future and its inputs are the only way to judge it. Today: "last
   * season's points". After Epic F: the projection engine and its recipe.
   */
  basis: z.string().min(1),
  /** The scoring preset the values were computed under. A PPR list is not a standard one. */
  scoring: z.string(),
  /** How many of `players` carry a value. The rest are draftable and unranked (§4.5). */
  ranked: z.number().int(),
  players: z.array(RankedPlayer),
  updatedAt: z.string(),
});
export type HouseRanking = z.infer<typeof HouseRanking>;

/** One player's stored production, as the ranking reads it. */
export interface RankingInput {
  playerId: string;
  name: string;
  positions: readonly string[];
  proTeam: string | null;
  /** Points in the basis season, already scored. Null when there is nothing. */
  points: number | null;
  /** Games the points came from, for the mean. Zero when nothing. */
  games: number;
  /**
   * The overall pick the pro league drafted him with, null undrafted or not
   * known: what orders the players the basis had nothing for (0087).
   */
  proDraftPick?: number | null;
}

/**
 * Order the pool.
 *
 * MEAN PER GAME, NOT TOTAL, and the difference is the whole quality of the
 * list. A total rewards availability: a mediocre player who started seventeen
 * games outranks a good one who missed six, and a draft built on that hands
 * everybody last year's iron men. The mean asks the question a manager is
 * actually asking — how good is he when he plays — and availability is a
 * separate thing they can see for themselves.
 *
 * A FLOOR ON GAMES, because a mean over one good afternoon is not evidence.
 * Below it a player is ranked by that mean but behind everybody who cleared it,
 * which keeps them in the list — unranked is not excluded (§4.5) — without
 * letting one game put them in round two.
 *
 * A PLAYER THE BASIS HAD NOTHING FOR — a rookie, above all — goes where the
 * pro league drafted him, undrafted last, still below everybody it had a
 * season for. It was the order of their names, which made a dynasty's rookie
 * draft the alphabet (PLAN-2026-10-27-the-next-season §2.4 rule 3).
 *
 * TOTAL ORDER, ALWAYS. Ties break by name then id, for the reason the lab
 * week's squads do: a ranking that came back in a different order on a second
 * request would produce a different draft from the same inputs, and the
 * replayable board is the product.
 */
export function rankPlayers(
  input: readonly RankingInput[],
  options: { minGames?: number } = {},
): RankedPlayer[] {
  const minGames = options.minGames ?? 4;
  const scored = input.map((p) => {
    const mean = p.points !== null && p.games > 0 ? p.points / p.games : null;
    return { ...p, mean, thin: p.games < minGames };
  });

  scored.sort((a, b) => {
    // Everybody who cleared the floor, then everybody who did not.
    if (a.thin !== b.thin) return a.thin ? 1 : -1;
    const am = a.mean ?? -1;
    const bm = b.mean ?? -1;
    if (am !== bm) return bm - am;
    if (a.mean === null && b.mean === null) {
      const ap = a.proDraftPick ?? Number.MAX_SAFE_INTEGER;
      const bp = b.proDraftPick ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
    }
    if (a.name !== b.name) return a.name < b.name ? -1 : 1;
    return a.playerId < b.playerId ? -1 : 1;
  });

  return scored.map((p, i) => ({
    playerId: p.playerId,
    name: p.name,
    positions: [...p.positions],
    proTeam: p.proTeam,
    rank: i + 1,
    value: p.mean === null ? null : Math.round(p.mean * 100) / 100,
  }));
}
